import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { TrendGranularity } from "../../utils/bucket";

export const TREND_GRANULARITY_LABEL: Record<TrendGranularity, string> = {
  day: "by day",
  week: "by week",
  month: "by month",
  quarter: "by quarter",
};

const TREND_TOOLTIP_PREPOSITION: Record<TrendGranularity, string> = {
  day: "on",
  week: "in week of",
  month: "in",
  quarter: "in",
};

interface TrendChartProps {
  buckets: { label: string; date: Date; count: number }[];
  color: string;
  granularity: TrendGranularity;
  height?: number;
}

export default function TrendChart({
  buckets,
  color,
  granularity,
  height = 140,
}: TrendChartProps) {
  const prep = TREND_TOOLTIP_PREPOSITION[granularity];
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const barCount = buckets.length;
  if (barCount === 0) return null;
  const labelIndexes = new Set(
    [0, Math.floor(barCount / 2), barCount - 1].filter(
      (i) => i >= 0 && i < barCount,
    ),
  );
  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: "4px",
          height,
        }}
      >
        {buckets.map((b, i) => {
          const h = (b.count / max) * (height - 12);
          // Centred over the bar, except near either end, where a centred
          // tooltip spills past the card edge: pin it to the bar's inner side.
          const edge =
            i < barCount * 0.2 ? "start" : i >= barCount * 0.8 ? "end" : "mid";
          return (
            <Box
              key={i}
              sx={{
                flex: 1,
                minWidth: 2,
                height: `${Math.max(2, h)}px`,
                bgcolor: b.count === 0 ? alpha(color, 0.15) : color,
                borderRadius: "4px 4px 0 0",
                transition: "background-color .2s",
                position: "relative",
                "&:hover::after": b.count
                  ? {
                      content: `"${b.count} ${prep} ${b.label}"`,
                      position: "absolute",
                      bottom: "calc(100% + 4px)",
                      ...(edge === "start"
                        ? { left: 0 }
                        : edge === "end"
                          ? { right: 0 }
                          : { left: "50%", transform: "translateX(-50%)" }),
                      // Inverse of the surface in either mode: near-black on
                      // light, near-white on dark.
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.92),
                      color: "background.paper",
                      fontSize: 11,
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.75,
                      whiteSpace: "nowrap",
                      zIndex: 2,
                    }
                  : undefined,
              }}
              aria-label={`${b.count} submissions ${prep} ${b.label}`}
            />
          );
        })}
      </Box>
      <Box
        sx={{
          display: "flex",
          mt: 0.75,
          justifyContent: "space-between",
        }}
      >
        {buckets.map((b, i) =>
          labelIndexes.has(i) ? (
            <Typography key={i} variant="caption" color="text.secondary">
              {b.label}
            </Typography>
          ) : null,
        )}
      </Box>
    </Box>
  );
}
